import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'

interface MembershipFlowContextValue {
  open: boolean
  openMembership: () => void
  closeMembership: () => void
  restoreFocus: () => void
  setRestoreTarget: (el: HTMLElement | null) => void
}

const MembershipFlowContext = createContext<MembershipFlowContextValue | null>(null)

export function MembershipFlowProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const restoreTargetRef = useRef<HTMLElement | null>(null)

  const setRestoreTarget = useCallback((el: HTMLElement | null) => {
    restoreTargetRef.current = el
  }, [])

  const restoreFocus = useCallback(() => {
    const target = restoreTargetRef.current
    if (target && typeof target.focus === 'function') {
      window.setTimeout(() => target.focus(), 0)
    }
  }, [])

  const openMembership = useCallback(() => setOpen(true), [])
  const closeMembership = useCallback(() => setOpen(false), [])

  const value = useMemo(
    () => ({
      open,
      openMembership,
      closeMembership,
      restoreFocus,
      setRestoreTarget,
    }),
    [open, openMembership, closeMembership, restoreFocus, setRestoreTarget],
  )

  return <MembershipFlowContext.Provider value={value}>{children}</MembershipFlowContext.Provider>
}

export function useMembershipFlow(): MembershipFlowContextValue {
  const ctx = useContext(MembershipFlowContext)
  if (!ctx) {
    throw new Error('useMembershipFlow must be used within MembershipFlowProvider')
  }
  return ctx
}
